import * as React from 'react';
import { cn } from '../lib/cn';

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Element to render. Default `<p>`. Heading tags (`h1`–`h6`) render page /
   * section headings with correct document semantics — pair with a `size`
   * (e.g. `<Text as="h1" size="display">`) rather than relying on the tag's
   * default browser sizing (the DS resets it).
   */
  as?: 'p' | 'span' | 'div' | 'label' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * Font size, in --text-* token steps. `3xl`/`display`/`display-lg` are the
   * headline / hero steps (`display` == 34px / weight 900 — the guide kit head).
   */
  size?: 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'display' | 'display-lg';
  /** Color tone. */
  tone?: 'default' | 'muted' | 'strong' | 'faint' | 'danger' | 'accent';
  /** Font weight. */
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  /** Horizontal alignment. */
  align?: 'start' | 'center' | 'end';
  /** Monospace family (IDs, figures). */
  mono?: boolean;
  /** Uppercase + wide tracking (eyebrows, table headers). */
  uppercase?: boolean;
  /** Tabular (lining) figures — aligned columns of numbers. */
  numeric?: boolean;
  /** Truncate to one line with an ellipsis (requires a bounded width). */
  truncate?: boolean;
  /** Never wrap (single-line, may overflow). */
  nowrap?: boolean;
  /**
   * Line-through — a comparison price next to the actual one ("~~49 €~~
   * 24,50 €"). Purely visual: screen readers do not announce it, so the
   * surrounding text must carry the meaning (e.g. an aria-label "statt 49 €").
   */
  strike?: boolean;
}

/**
 * Typography primitive for body / helper / caption / heading text. Replaces
 * ad-hoc `<p style={{ fontSize, color, fontWeight, textAlign }}>` AND the
 * `text-* font-* uppercase truncate whitespace-nowrap tabular-nums` utility
 * classes in apps — compose with attributes. Classes are namespaced
 * (`ui-text`) to avoid colliding with Tailwind's `text-*` utilities.
 */
export const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      as: Tag = 'p',
      // No default: a bare <Text> emits no data-size and inherits the ambient
      // font-size (e.g. .card-body's --text-sm). An EXPLICIT size="base" DOES
      // emit the attr → renders 14px regardless of container (fixes links /
      // labels that shrank inside .card-body). See .ui-text[data-size] CSS.
      size,
      tone = 'default',
      weight,
      align = 'start',
      mono,
      uppercase,
      numeric,
      truncate,
      nowrap,
      strike,
      className,
      ...props
    },
    ref,
  ) => (
    <Tag
      // forwardRef across a union of intrinsic tags — the runtime element is correct.
      ref={ref as React.Ref<never>}
      className={cn('ui-text', className)}
      data-size={size}
      data-tone={tone !== 'default' ? tone : undefined}
      data-weight={weight}
      data-align={align !== 'start' ? align : undefined}
      data-mono={mono ? '' : undefined}
      data-uppercase={uppercase ? '' : undefined}
      data-numeric={numeric ? '' : undefined}
      data-truncate={truncate ? '' : undefined}
      data-nowrap={nowrap ? '' : undefined}
      data-strike={strike ? '' : undefined}
      {...props}
    />
  ),
);

Text.displayName = 'Text';
